import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

export const productAvailabiliyStatuses = ["unavailable", "available"] as const;
export type ProductAvailabiliyStatus = (typeof productAvailabiliyStatuses)[number];

export const addProductAvailabilitSerializer = Serializers.object({
  id: uuidSerializer,
  itemId: Serializers.string,
  status: Serializers.stringOneOf(...productAvailabiliyStatuses),
  posId: uuidSerializer,
  date: Serializers.datetime,
});

export type ProductAvailabiliy = SerializerValue<typeof addProductAvailabilitSerializer>;

export const allProductAvailabilitSerializer = Serializers.object({
  itemId: Serializers.string,
  itemName: Serializers.string,
  status: Serializers.stringOneOf(...productAvailabiliyStatuses),
  posName: Serializers.string,
  date: Serializers.datetime,
});

export type ProductAvailabiliyListObject = SerializerValue<typeof allProductAvailabilitSerializer>;

export const customerProductAvailabilitySerializer = Serializers.object({
  itemId: Serializers.string,
  itemName: Serializers.string,
  status: Serializers.stringOneOf(...productAvailabiliyStatuses),
  posName: Serializers.string,
  date: Serializers.datetime,
});

export const listedPosSerializer = Serializers.object({
  items: Serializers.object({
    itemId: Serializers.string,
    itemName: Serializers.string,
    gtin: Serializers.string.nullable(),
    status: Serializers.stringOneOf(...productAvailabiliyStatuses),
    date: Serializers.datetime,
  }).array(),
  customerId: uuidSerializer,
  googlePlaceId: Serializers.string.nullable(),
  listedName: Serializers.string,
  listedPhone: Serializers.string,
  listedWebsite: Serializers.string,
  latitude: Serializers.decimal.nullable(),
  longitude: Serializers.decimal.nullable(),
  country: Serializers.string,
  city: Serializers.string,
  street: Serializers.string,
  postalCode: Serializers.string,
});

export type ListedPos = SerializerValue<typeof listedPosSerializer>;

export const productAvailabilityReport = Serializers.object({
  posName: Serializers.string,
  posUuid: Serializers.string,
  regionName: Serializers.string.nullable(),
  products: Serializers.object({
    itemId: Serializers.string,
    itemName: Serializers.string,
    date: Serializers.datetime,
    status: Serializers.stringOneOf(...productAvailabiliyStatuses),
  }).array(),
});

export type ProductAvailabilityReportItem = SerializerValue<typeof productAvailabilityReport>;

export const productAvailabiliy = {
  listedPosQuery: HttpAction({
    path: "/publicPos",
    method: "get",
    qsSerializer: Serializers.object({
      searchByGtin: Serializers.string.array().asJsonString(),
      minimumLimit: Serializers.integer,
      boundingBox: Serializers.object({
        minLat: Serializers.float,
        maxLat: Serializers.float,
        minLng: Serializers.float,
        maxLng: Serializers.float,
      }).asJsonString(),
    }).partial(),
    valueSerializer: listedPosSerializer.array(),
  }),

  customerProductsAvailabilityQuery: HttpAction({
    path: "/productAvailability/customer/:customerId",
    method: "get",
    paramsSerializer: Serializers.object({
      customerId: uuidSerializer,
    }),
    requiresAuth: true as const,
    valueSerializer: customerProductAvailabilitySerializer.array(),
  }),

  productsAvailabilityReport: HttpAction({
    path: "/productsAvailabilityReport",
    method: "get",
    requiresAuth: true as const,
    qsSerializer: Serializers.object({
      regions: Serializers.string.array().asJsonString(),
      searchTerm: Serializers.string,
      startDate: Serializers.datetime,
      endDate: Serializers.datetime,
      limit: Serializers.integer,
      offset: Serializers.integer,
    }).partial(),
    valueSerializer: Serializers.object({
      items: productAvailabilityReport.array(),
      totalCount: Serializers.integer,
    }),
  }),

  addProductAvailability: HttpAction({
    path: "/productAvailability",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: addProductAvailabilitSerializer,
    valueSerializer: addProductAvailabilitSerializer,
  }),
};
